import Decimal from 'decimal.js';

import { parseIntegerFormValue } from '@/components/utils/inputUtils';
import { getTypeOrUndefined } from '@/modules/entities/EntitySubforms/utils/shared/common.utils';
import {
  AugmentedCreateCashflowInput,
  AugmentedUpdateCashflowInput,
} from '@/types/schema';

import {
  BOTH_CLIENTS_SENTINEL,
  CashflowDrawerFormShape,
  CashflowEndType,
  CashflowFrequency,
  CashflowIncomeOrExpense,
  CashflowValueType,
} from './CashflowDrawer.types';
import { Cashflows_CashflowFragment } from './graphql/Cashflows.generated';

export function private_getEndYearPayload({
  targetId,
  frequency,
  startYear,
  endYear,
  endType,
  endAtDeathClientProfileId,
}: CashflowDrawerFormShape): {
  afterBothDeaths?: boolean;
  endAtClientsDeathID?: string;
  endYear?: number;
} {
  const isClientTarget = getTargetType(targetId) === TargetType.ClientProfile;

  if (frequency === CashflowFrequency.OneTime)
    return { endYear: parseIntegerFormValue(startYear) ?? undefined };

  if (endType === CashflowEndType.Year)
    return { endYear: parseIntegerFormValue(endYear) ?? undefined };

  if (endType === CashflowEndType.BothDeaths && isClientTarget) {
    return { afterBothDeaths: true };
  }

  if (endType === CashflowEndType.Death && isClientTarget) {
    return { endAtClientsDeathID: endAtDeathClientProfileId };
  }

  if (endType === CashflowEndType.DeathOther && isClientTarget) {
    return { endAtClientsDeathID: endAtDeathClientProfileId };
  }

  // Entity termination case - no end year
  return {};
}

function getTargetPayload({ targetId }: CashflowDrawerFormShape): {
  entityID?: string;
  individualID?: string;
} {
  const targetType = getTargetType(targetId);

  if (targetType === TargetType.ClientProfile)
    return { individualID: targetId };

  if (targetType === TargetType.Entity) return { entityID: targetId };

  return {};
}

export function mapFormDataToUpdatePayload(
  formData: CashflowDrawerFormShape,
  cashflowId: string
): AugmentedUpdateCashflowInput {
  const { startYear, growthRate, name } = formData;
  const { afterBothDeaths, endAtClientsDeathID, endYear } =
    private_getEndYearPayload(formData);

  const { entityID, individualID } = getTargetPayload(formData);

  const isDollar = formData.valueType === CashflowValueType.Dollar;
  const isIncome = formData.incomeOrExpense === CashflowIncomeOrExpense.Income;

  const transferValue = (() => {
    if (isDollar) {
      return isIncome ? formData.amount : formData.amount?.negated();
    }
    return null;
  })();

  const transferPercentage = (() => {
    if (!isDollar) {
      return isIncome ? formData.percentage : formData.percentage?.negated();
    }
    return null;
  })();

  return {
    id: cashflowId,
    update: {
      displayName: name,
      entityID,
      individualID,
      clearEntity: !entityID,
      clearIndividual: !individualID,
      afterBothDeaths,
      endAtClientsDeathID,
      endYear,
      clearEndYear: !endYear,
      clearEndAtClientsDeath: !endAtClientsDeathID,
      clearAfterBothDeaths: !afterBothDeaths,
      startYear: parseIntegerFormValue(startYear),
      growthPercent: getTypeOrUndefined(growthRate),
      clearTransferValue: !isDollar,
      transferValue,
      clearTransferPercentage: isDollar,
      transferPercentage,
    },
  };
}

export function mapFormDataToCreatePayload(
  formData: CashflowDrawerFormShape,
  householdId: string | null
): AugmentedCreateCashflowInput {
  const targetPayload = getTargetPayload(formData);
  const endYearPayload = private_getEndYearPayload(formData);

  const { startYear, growthRate, name } = formData;

  if (!householdId) {
    throw new Error('No household available to create cashflow');
  }

  return {
    create: {
      ...targetPayload,
      ...endYearPayload,
      displayName: name,
      householdID: householdId,
      startYear: parseIntegerFormValue(startYear),
      growthPercent: getTypeOrUndefined(growthRate),
      transferValue:
        formData.valueType === CashflowValueType.Dollar
          ? formData.incomeOrExpense === CashflowIncomeOrExpense.Income
            ? formData.amount
            : formData.amount?.negated()
          : null,
      transferPercentage:
        formData.valueType === CashflowValueType.Percent
          ? formData.incomeOrExpense === CashflowIncomeOrExpense.Income
            ? formData.percentage
            : formData.percentage?.negated()
          : null,
    },
  };
}

export enum TargetType {
  Entity = 'entity',
  ClientProfile = 'cp',
}

export function getTargetType(targetId: string): TargetType | null {
  if (targetId.toLowerCase().startsWith('entity_')) {
    return TargetType.Entity;
  } else if (targetId.toLowerCase().startsWith('cp_')) {
    return TargetType.ClientProfile;
  }

  return null;
}

export function mapDataToCashflowDrawer(
  data: Cashflows_CashflowFragment
): CashflowDrawerFormShape {
  let endAtDeath: CashflowDrawerFormShape['endAtDeathClientProfileId'] = '';
  let endType: CashflowDrawerFormShape['endType'] = CashflowEndType.Year;

  if (data.endAtClientsDeath?.id) {
    endAtDeath = data.endAtClientsDeath.id;
    // If other client, set end type to death-other
    if (data.endAtClientsDeath.id !== data.individual?.id) {
      endType = CashflowEndType.DeathOther;
    } else {
      endType = CashflowEndType.Death;
    }
  } else if (data.afterBothDeaths) {
    endAtDeath = BOTH_CLIENTS_SENTINEL;
    endType = CashflowEndType.BothDeaths;
  } else if (!data.endYear && data.entity) {
    endType = CashflowEndType.TrustTermination;
  }

  return {
    amount: data.transferValue?.abs() ?? null,
    percentage: data.transferPercentage?.abs() ?? null,
    valueType: data.transferValue
      ? CashflowValueType.Dollar
      : CashflowValueType.Percent,
    endAtDeathClientProfileId: endAtDeath,
    name: data.displayName,
    targetId: data.individual?.id || data.entity?.id || '',
    growthRate: data.growthPercent || new Decimal(0),
    frequency:
      data.startYear === data.endYear
        ? CashflowFrequency.OneTime
        : CashflowFrequency.Annual,
    startYear: data.startYear || new Date().getFullYear(),
    endType,
    endYear: data.endYear || new Date().getFullYear(),
    incomeOrExpense: (
      data.transferValue ||
      data.transferPercentage ||
      new Decimal(0)
    ).greaterThanOrEqualTo(0)
      ? CashflowIncomeOrExpense.Income
      : CashflowIncomeOrExpense.Expense,
  };
}
